<script setup lang="ts">
import { computed, shallowRef, watchEffect } from 'vue';
import { CompanySide } from '@/modules/user-accounts/types/user-accounts';
import { formatCompanyName } from '@/modules/user-accounts/helpers/user-accounts';
import { CompanyInfo } from '@/modules/common/models';
import { useUserAccountsService } from '@/modules/user-accounts/services/user-accounts-api.service';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';

const userAccountsService = useUserAccountsService();
const snackbarManager = useSnackbarManager();

const props = withDefaults(
  defineProps<{
    value: CompanyInfo | null;
    multiple?: boolean;
    label?: string;
    autofocus?: boolean;
    disabled?: boolean;
    clearable?: boolean;
    dense?: boolean;
    errorMessages?: string | string[];
    placeholder?: string;
    prependItem?: CompanyInfo;
    // search through borrower traders; lender traders or both
    side?: CompanySide;
    sponsored?: boolean;
    includeSponsored?: boolean;
    onlyBilateralSettlementOnAurora?: boolean;
    excludeCompanyId?: string;
    excludeCompanyIds?: string[];
    rules?: Array<string | boolean | ((value: CompanyInfo | null) => string | boolean)>;
  }>(),
  {
    value: null,
    multiple: false,
    label: '',
    autofocus: false,
    disabled: false,
    clearable: false,
    dense: false,
    errorMessages: undefined,
    placeholder: undefined,
    prependItem: undefined,
    side: 'ALL',
    sponsored: false,
    includeSponsored: false,
    onlyBilateralSettlementOnAurora: false,
    excludeCompanyId: undefined,
    excludeCompanyIds: undefined,
    rules: undefined,
  }
);

const emit = defineEmits<{
  (e: 'input', item: CompanyInfo | null): void;
  (e: 'change', item: CompanyInfo): void;
  (e: 'blur', event: Event): void;
}>();

const isLoading = shallowRef(false);
const queriedItems = shallowRef<CompanyInfo[]>([]);
const searchTerm = shallowRef('');

const filteredItems = computed<CompanyInfo[]>(() => {
  const lowerSearchTerm = searchTerm.value.toLowerCase();
  return (
    queriedItems.value
      // filter by what the user typed
      .filter(
        (company) =>
          company.companyName.toLowerCase().includes(lowerSearchTerm) ||
          (company.displayId && company.displayId.toLowerCase().includes(lowerSearchTerm))
      )
      .filter(
        (company) =>
          (!props.excludeCompanyId || company.companyId !== props.excludeCompanyId) &&
          (!props.excludeCompanyIds || !props.excludeCompanyIds.includes(company.companyId))
      )
  );
});

const autocompleteItems = computed<CompanyInfo[]>(() => {
  return props.prependItem ? [props.prependItem, ...filteredItems.value] : filteredItems.value;
});

watchEffect(search);

async function search(): Promise<void> {
  isLoading.value = true;
  try {
    const { data } = await userAccountsService.searchCounterparties(
      '', // we always search for all companies, and later filter by what the user typed
      props.side,
      props.sponsored,
      props.onlyBilateralSettlementOnAurora,
      props.includeSponsored
    );

    queriedItems.value = data;
  } catch (err: unknown) {
    queriedItems.value = [];
    snackbarManager.error((err as Error).message);
  }
  isLoading.value = false;
}

function onItemSelected(item: CompanyInfo | null): void {
  emit('input', item);
  searchTerm.value = '';
}

function onChange(item: CompanyInfo): void {
  emit('change', item);
}
</script>

<template>
  <VAutocomplete
    auto-select-first
    :autofocus="autofocus"
    class="counterparty-search"
    :clearable="clearable"
    :dense="dense"
    :disabled="disabled"
    :error-messages="errorMessages"
    hide-no-data
    item-value="displayId"
    :items="autocompleteItems"
    :label="label"
    :loading="isLoading"
    no-filter
    :placeholder="placeholder || 'search...'"
    return-object
    :rules="rules"
    :value="value"
    @blur="emit('blur', $event)"
    @change="onChange"
    @input="onItemSelected"
    @update:search-input="(value) => (searchTerm = value || '')"
  >
    <template #selection="data">
      {{ formatCompanyName(data.item) }}
    </template>
    <template #item="data">
      <VListItemContent>
        <VListItemTitle>
          {{ formatCompanyName(data.item) }}
        </VListItemTitle>
      </VListItemContent>
    </template>
  </VAutocomplete>
</template>

<style lang="scss" scoped>
::v-deep {
  // Autocomplete shows both selected value
  // and user input text at the same time

  // Only show input when expanded
  [aria-expanded='true'] .v-select__selections {
    text-indent: -999em;
  }

  // Only show selected value when not expanded
  [aria-expanded='false'] .v-select__selections input {
    text-indent: -999em;
    min-width: unset;
    &::placeholder {
      text-indent: 0;
    }
  }

  .v-select__selections {
    white-space: nowrap;
    flex-wrap: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}
</style>
